export interface EllipseGeometry {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

/** SVG viewBox dimensions used for all geometry calculations. */
export const VIEWBOX_WIDTH = 500;
export const VIEWBOX_HEIGHT = 280;

/**
 * Proportional layout data derived from the original GoJS diagram.
 * GoJS used a coordinate system centered roughly at (0,0) with:
 *   ℕ: loc=-75,0  w=110  h=95
 *   ℤ: loc=-45,0  w=215  h=145
 *   ℚ: loc=-10,0  w=330  h=195
 *   ℝ: loc= 30,0  w=440  h=250
 *
 * We normalise these to fractions of the outermost ellipse and then
 * scale to the SVG viewBox, centering vertically.
 */
const LAYOUT_4 = [
  { cxRatio: -0.4773, rxRatio: 0.25, ryRatio: 0.38 }, // ℕ  (GoJS loc=-75, R_cx=30, R_rx=220 → (-75-30)/220)
  { cxRatio: -0.3409, rxRatio: 0.489, ryRatio: 0.58 }, // ℤ  (GoJS loc=-45 → (-45-30)/220)
  { cxRatio: -0.1818, rxRatio: 0.75, ryRatio: 0.78 }, // ℚ  (GoJS loc=-10 → (-10-30)/220)
  { cxRatio: 0.0, rxRatio: 1.0, ryRatio: 1.0 }, // ℝ  (GoJS loc=30, reference)
];

/**
 * Computes SVG ellipse geometries for the given number of sets.
 * Supports 1–4 sets. The outermost ellipse fills the viewBox.
 */
export function computeEllipseGeometries(setCount: number): EllipseGeometry[] {
  const maxRx = (VIEWBOX_WIDTH - 40) / 2; // leave 20px padding each side
  const maxRy = (VIEWBOX_HEIGHT - 20) / 2; // leave 10px padding top/bottom
  const centerY = VIEWBOX_HEIGHT / 2;
  const centerX = VIEWBOX_WIDTH / 2;

  // Use the last N entries from the 4-set layout
  const layouts = LAYOUT_4.slice(4 - Math.min(setCount, 4));

  return layouts.map((l) => ({
    cx: centerX + l.cxRatio * maxRx,
    cy: centerY,
    rx: l.rxRatio * maxRx,
    ry: l.ryRatio * maxRy,
  }));
}

/**
 * Computes the label (set symbol) position for a given set.
 * Labels are placed in the visible "crescent" region of each set,
 * offset to the right of any inner set.
 */
export function computeLabelPosition(setIndex: number, geometries: EllipseGeometry[]): { x: number; y: number } {
  const geom = geometries[setIndex];
  if (setIndex === 0) {
    // Innermost set — label at center
    return { x: geom.cx, y: geom.cy - 2 };
  }
  // Place label halfway between the right edge of inner set and right edge of this set
  const inner = geometries[setIndex - 1];
  const innerRight = inner.cx + inner.rx;
  const thisRight = geom.cx + geom.rx;
  return { x: (innerRight + thisRight) / 2, y: geom.cy - 2 };
}

/**
 * Computes the examples text position, directly below the label.
 */
export function computeExamplesPosition(setIndex: number, geometries: EllipseGeometry[]): { x: number; y: number } {
  const labelPos = computeLabelPosition(setIndex, geometries);
  return { x: labelPos.x, y: labelPos.y + 28 };
}

export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
